import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useActiveCategories, useMeta } from "../lib/meta";
import type { CapStatus, Suggestion } from "../lib/types";
import { formatINR, formatPct, formatPoints } from "../lib/format";
import { Badge, EmptyState, ErrorBanner, PageHeader, Panel, Spinner } from "../components/ui";
import { CapTracker } from "../components/CapTracker";

interface Result {
  suggestions: Suggestion[];
  summary: {
    bestCardName: string;
    netValue: number;
    effectiveRatePct: number;
    advantageOverNext: number | null;
    runnerUpName: string | null;
  } | null;
}

export default function OptimizerPage() {
  const meta = useMeta();
  const categories = useActiveCategories();
  const quickAmounts = meta?.preferences.quickAmounts ?? [];
  const [amount, setAmount] = useState("5000");
  const [category, setCategory] = useState("");
  const [merchant, setMerchant] = useState("");
  const [channel, setChannel] = useState<"ONLINE" | "OFFLINE">("OFFLINE");
  const [isInternational, setInternational] = useState(false);
  const [useBestValue, setUseBestValue] = useState(false);
  const [ready, setReady] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  async function run(e?: React.FormEvent) {
    e?.preventDefault();
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter an amount greater than zero");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const r = await api.post<Result>("/optimizer", {
        amount: value,
        category,
        merchant,
        channel,
        isInternational,
        useBestValue,
      });
      setResult(r);
      setExpanded(r.suggestions[0]?.cardId ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not work out the best card");
    } finally {
      setBusy(false);
    }
  }

  // Seed the form from your saved preferences, then run once so the page is
  // never just a blank form.
  useEffect(() => {
    if (!meta || ready) return;
    setUseBestValue(meta.preferences.optimiserUsesBestValue);
    const first = meta.categories.find((c) => !c.isArchived)?.key ?? "";
    setCategory((c) => c || first);
    setReady(true);
  }, [meta, ready]);

  useEffect(() => {
    if (ready && category) void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  if (!meta) return <Spinner />;

  const best = result?.suggestions[0];

  return (
    <>
      <PageHeader
        title="Which card should I use?"
        subtitle="Ranks your cards by what this spend is actually worth — points value, milestones it would unlock, minus forex markup."
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <Panel className="mb-6">
        <form onSubmit={run} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="label" htmlFor="amount">Amount (₹)</label>
              <input
                id="amount"
                className="input tnum"
                type="number"
                min={1}
                step={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="category">Category</label>
              <select id="category" className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="merchant">Merchant (optional)</label>
              <input
                id="merchant"
                className="input"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                placeholder="e.g. Amazon, Swiggy, SmartBuy"
              />
            </div>
            <div>
              <label className="label" htmlFor="channel">Channel</label>
              <select
                id="channel"
                className="input"
                value={channel}
                onChange={(e) => setChannel(e.target.value as "ONLINE" | "OFFLINE")}
              >
                <option value="OFFLINE">In store / swipe</option>
                <option value="ONLINE">Online</option>
              </select>
            </div>
          </div>

          {quickAmounts.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-ink-500">Quick amounts:</span>
            {quickAmounts.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setAmount(String(q))}
                className={`chip-action ring-1 ${
                  Number(amount) === q ? "bg-accent-50 text-accent-800 ring-accent-200" : "bg-white text-ink-600 ring-ink-200 hover:bg-ink-50"
                }`}
              >
                {formatINR(q, { compact: true })}
              </button>
            ))}
            <Link to="/settings" className="text-xs text-ink-400 underline hover:text-ink-600">edit</Link>
          </div>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={isInternational}
                onChange={(e) => setInternational(e.target.checked)}
              />
              International spend (applies forex markup)
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={useBestValue}
                onChange={(e) => setUseBestValue(e.target.checked)}
              />
              Value points at my best redemption rate
            </label>
            <button className="btn-primary ml-auto" disabled={busy}>
              {busy ? "Working…" : "Find the best card"}
            </button>
          </div>
        </form>
      </Panel>

      {busy && !result && <Spinner label="Comparing your cards…" />}

      {result && result.suggestions.length === 0 && (
        <EmptyState
          title="No active cards to compare"
          hint="Add a card first — the optimiser ranks the cards you hold."
          action={<Link to="/cards" className="btn-primary">Add a card</Link>}
        />
      )}

      {best && result?.summary && (
        <div className="mb-6 rounded-xl border border-accent-200 bg-accent-50 p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-accent-800">Use this card</p>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="h-3 w-3 rounded-sm" style={{ background: best.cardColor }} aria-hidden />
            <h2 className="text-xl font-semibold text-ink-950">{best.cardName}</h2>
            <span className="tnum text-sm text-ink-600">
              {formatINR(best.netValue)} back · {formatPct(best.effectiveRatePct)} effective
            </span>
          </div>
          <p className="mt-2 text-sm text-ink-700">
            Earns {formatPoints(best.points)} {best.currencyName}
            {best.bonusPoints > 0 && ` (${formatPoints(best.bonusPoints)} of them bonus)`}
            {best.milestoneBonus > 0 && ` and tips you over a milestone worth ${formatINR(best.milestoneBonus)}`}
            {result.summary.advantageOverNext != null && result.summary.runnerUpName && (
              result.summary.advantageOverNext > 0
                ? ` — ${formatINR(result.summary.advantageOverNext)} better than ${result.summary.runnerUpName}.`
                : ` — level with ${result.summary.runnerUpName}.`
            )}
          </p>
        </div>
      )}

      {result && result.suggestions.length > 0 && (
        <Panel title="Full ranking" subtitle="Net value = points value + milestone unlocked − forex markup">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">#</th>
                  <th className="th">Card</th>
                  <th className="th text-right">Points</th>
                  <th className="th text-right">Points value</th>
                  <th className="th text-right">Milestone</th>
                  <th className="th text-right">Net value</th>
                  <th className="th text-right">Effective</th>
                  <th className="th"><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {result.suggestions.map((s, i) => (
                  <Fragment key={s.cardId}>
                    <tr className={i === 0 ? "bg-accent-50/40" : undefined}>
                      <td className="td tnum text-ink-400">{i + 1}</td>
                      <td className="td">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.cardColor }} aria-hidden />
                          <span>
                            <span className="font-medium text-ink-900">{s.cardName}</span>
                            <span className="block text-xs text-ink-500">{s.appliedRule}</span>
                          </span>
                        </span>
                      </td>
                      <td className="td tnum text-right">
                        {formatPoints(s.points)}
                        {s.bonusPoints > 0 && (
                          <span className="block text-xs font-normal text-ink-500">
                            {formatPoints(s.basePoints)} + {formatPoints(s.bonusPoints)} bonus
                          </span>
                        )}
                      </td>
                      <td className="td tnum text-right">{formatINR(s.pointsValue)}</td>
                      <td className="td tnum text-right">
                        {s.milestoneBonus > 0 ? (
                          <span className="text-emerald-700">+{formatINR(s.milestoneBonus)}</span>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="td tnum text-right font-semibold">{formatINR(s.netValue)}</td>
                      <td className="td tnum text-right">{formatPct(s.effectiveRatePct)}</td>
                      <td className="td text-right">
                        <button
                          className="text-xs font-medium text-accent-700 underline"
                          onClick={() => setExpanded(expanded === s.cardId ? null : s.cardId)}
                          aria-expanded={expanded === s.cardId}
                        >
                          {expanded === s.cardId ? "Hide" : "Why"}
                        </button>
                      </td>
                    </tr>
                    {expanded === s.cardId && (
                      <tr className="bg-ink-50/60">
                        <td className="td" />
                        <td className="td" colSpan={7}>
                          <div className="space-y-3">
                            {s.breakdown.length > 0 ? (
                              <ul className="space-y-1 text-xs text-ink-600">
                                {s.breakdown.map((leg, idx) =>
                                  leg.ruleId === null && leg.points === 0 ? (
                                    <li key={idx} className="tnum text-amber-800">
                                      {formatINR(leg.spend)} over the cap ({leg.ruleName}) — {leg.rateLabel}
                                    </li>
                                  ) : (
                                    <li key={idx} className="tnum">
                                      {formatINR(leg.spend)} at {leg.rateLabel} ({leg.ruleName}) →{" "}
                                      <strong className="text-ink-900">{formatPoints(leg.points)} pts</strong>
                                    </li>
                                  ),
                                )}
                              </ul>
                            ) : (
                              <p className="text-xs text-ink-500">This spend earns nothing on this card.</p>
                            )}

                            {s.cap && <CapLine cap={s.cap} />}

                            {s.milestonesUnlocked.length > 0 && (
                              <div className="text-xs text-emerald-800">
                                {s.milestonesUnlocked.map((m) => (
                                  <p key={m.name}>
                                    ✓ Completes <strong>{m.name}</strong> ({m.rewardDescription}) — you were{" "}
                                    {m.unit === "TRANSACTIONS"
                                      ? `${Math.round(m.shortfallBefore)} transaction${Math.round(m.shortfallBefore) === 1 ? "" : "s"}`
                                      : formatINR(m.shortfallBefore)}{" "}
                                    short.
                                  </p>
                                ))}
                              </div>
                            )}

                            {s.forexCost > 0 && (
                              <p className="text-xs text-red-700">− {formatINR(s.forexCost)} foreign currency markup</p>
                            )}

                            {s.warnings.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {s.warnings.map((w) => (
                                  <Badge key={w} tone={s.excluded ? "critical" : "warning"}>{w}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <CapTracker className="mt-6" compact />
    </>
  );
}

/** Where the cap this card would draw on stands, before and after this spend. */
function CapLine({ cap }: { cap: CapStatus }) {
  const unit = (n: number) =>
    cap.type === "SPEND" ? formatINR(n) : `${formatPoints(n)}${cap.appliesTo === "BONUS" ? " bonus" : ""} pts`;
  const left = Math.max(0, cap.limit - cap.usedBefore);
  return (
    <p className="text-xs text-ink-600">
      <span className="font-medium text-ink-900">{cap.name} cap:</span> {unit(left)} of {unit(cap.limit)} left
      before this; this uses {unit(cap.usedByThis)}, leaving <span className="font-medium text-ink-900">{unit(cap.remainingAfter)}</span>.
      {cap.appliesTo === "BONUS"
        ? " The base points are earned regardless."
        : cap.overflow === "NOTHING"
          ? " Past it, this card earns nothing on these."
          : " Past it, the base rate applies."}
    </p>
  );
}
